import {
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { select, Store } from '@ngrx/store';

import { TranslateService } from '@ngx-translate/core';

import { DataObject, FeedRemovedItem } from '../../../models/core.models';

import {
  fetchRemovedItems,
  deleteRemovedItem,
  clearAllRemovedItems,
} from '../../../actions/feed.actions';

import { AppState } from 'src/app/reducers';
import {
  selectCurrentObject,
  selectRemovedItemsByFeed,
} from '../../../reducers';

import { distinctUntilChanged, Observable, tap } from 'rxjs';
import { NomObjectClass } from 'src/app/core/constants';
import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';
import { DialogService } from '@sui/dialog';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-removed-items',
  templateUrl: './removed-items.component.html',
  styleUrl: './removed-items.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RemovedItemsComponent {
  removedItems$: Observable<FeedRemovedItem[]>;

  NomObjectClass = NomObjectClass;
  currentObject: DataObject;

  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.removedItems$ = this.store.select(selectRemovedItemsByFeed);
  }

  ngOnInit(): void {
    this.store
      .pipe(
        select(selectCurrentObject),
        // only update when the object identity actually changes
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        // side-effect: store locally
        tap((obj) => (this.currentObject = obj)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store.dispatch(fetchRemovedItems({ object: this.currentObject }));
  }

  removeSingleItem(item: FeedRemovedItem): void {
    const title = this.translateService.instant(
      'removed-items.confirmation.title',
    );
    const message = this.translateService.instant(
      'removed-items.confirmation.are-you-sure-you-want-to-delete-this-item',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(
            deleteRemovedItem({
              feedId: this.currentObject.id,
              objectId: item.object_id,
              objectClassId: item.object_class_id,
            }),
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  removeAllItems(): void {
    const title = this.translateService.instant(
      'removed-items.confirmation.title',
    );
    const message = this.translateService.instant(
      'removed-items.confirmation.are-you-sure-you-want-to-delete-all-items',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(
            clearAllRemovedItems({ object: this.currentObject }),
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  trackByRemovedItem(index: number, item: FeedRemovedItem): string {
    return `${item.object_class_id}-${item.object_id}`;
  }
}
